import { useEffect, useState } from "react";
import {
  loadTripLiveTracking,
  subscribeTripLiveTracking,
} from "../services/firestoreTracking.service";

const LIVE_TRACKING_REFRESH_MS = 5000;

export function useTripLiveTracking(tripId, enabled = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(tripId) && enabled);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tripId || !enabled) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);
    setData(null);
    let cancelled = false;

    const refreshSnapshot = async () => {
      try {
        const nextValue = await loadTripLiveTracking(tripId);
        if (cancelled) return;
        setData(nextValue);
        setLoading(false);
      } catch (nextError) {
        if (cancelled) return;
        setError(nextError);
        setLoading(false);
      }
    };

    const unsubscribe = subscribeTripLiveTracking(
      tripId,
      (nextValue) => {
        if (cancelled) return;
        setData(nextValue);
        setLoading(false);
      },
      (nextError) => {
        if (cancelled) return;
        setError(nextError);
        setLoading(false);
      }
    );

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === "visible") {
        refreshSnapshot();
      }
    };
    window.addEventListener("focus", refreshSnapshot);
    window.addEventListener("online", refreshSnapshot);
    document.addEventListener("visibilitychange", handleVisibilityRefresh);
    const refreshInterval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshSnapshot();
      }
    }, LIVE_TRACKING_REFRESH_MS);

    return () => {
      cancelled = true;
      unsubscribe?.();
      window.removeEventListener("focus", refreshSnapshot);
      window.removeEventListener("online", refreshSnapshot);
      document.removeEventListener("visibilitychange", handleVisibilityRefresh);
      window.clearInterval(refreshInterval);
    };
  }, [tripId, enabled]);

  return { data, loading, error };
}
